import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { makeForm } from "@/lib/upload";
import { toast } from "sonner";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const fromDoc = (src) => /^(CPCV carregado|Adenda)/.test(src || "");

// Crédito por aprovar: o servidor responde 409 e pedimos confirmação antes de marcar o CPCV.
export async function withCreditConfirm(call) {
  try { return await call(false); }
  catch (e) {
    const d = e.response?.data?.detail;
    if (e.response?.status !== 409 || d?.code !== "credit_pending") throw e;
    if (!window.confirm(d.message)) return null;
    return call(true);
  }
}

export function DatesDialog({ p, open, onClose, onSaved }) {
  const c = p.cpcv || {};
  const [f, setF] = useState({ cpcv_date: c.date || "", cpcv_deadline: c.limit || p.cpcv_deadline || "", deed_date: p.deed_date || "", has_credit: p.has_credit ?? null });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  if (!open) return null;
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await api.post(`/processes/${p.id}/documents/cpcv_adenda/upload`, await makeForm([["file", file]])); toast.success("Adenda carregada e lida"); onSaved(); }
    catch (ex) { toast.error(apiErr(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = async () => {
    const body = { deed_date: f.deed_date || null };
    if (!fromDoc(c.date_source)) body.cpcv_date = f.cpcv_date || null;
    if (!fromDoc(c.limit_source)) body.cpcv_deadline = f.cpcv_deadline || null;
    if (f.cpcv_date && !c.date) {
      if (f.has_credit == null) { toast.error("Indique o financiamento: com crédito aprovado ou sem crédito."); return; }
      Object.assign(body, { has_credit: f.has_credit, credit_answer: f.has_credit ? "sim" : "nao" });
    }
    try {
      const r = await withCreditConfirm((ok) => api.put(`/processes/${p.id}`, ok ? { ...body, confirm_credit: true } : body));
      if (!r) return;
      toast.success("Datas atualizadas"); onSaved();
    }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const field = (k, label, src) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <DateInput value={f[k]} disabled={fromDoc(src)} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`dates-${k}`} />
      {src && <div className="text-[11px] text-slate-400 mt-0.5">Origem: {src}{fromDoc(src) ? " · para mudar, carregue o CPCV ou adenda correta" : ""}</div>}
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="dates-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-ec-green">Rever datas</DialogTitle>
          <DialogDescription>A escritura tem de ser feita até ao limite do CPCV, ou é preciso uma adenda.</DialogDescription>
        </DialogHeader>
        {field("cpcv_date", "Data do CPCV", c.date_source)}
        {f.cpcv_date && !c.date && (
          <div data-testid="dates-credit">
            <Label className="text-xs">Financiamento</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {[[true, "Com crédito aprovado"], [false, "Sem crédito"]].map(([v, l]) => (
                <button key={l} type="button" onClick={() => setF({ ...f, has_credit: v })} data-testid={`dates-credit-${v ? "sim" : "nao"}`}
                  className={`h-10 rounded-lg border text-sm font-semibold transition-colors ${f.has_credit === v ? "bg-ec-green text-white border-ec-green" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
          </div>
        )}
        {field("cpcv_deadline", "Limite do CPCV", c.limit_source)}
        {field("deed_date", "Data da escritura", null)}
        <div className="flex items-center justify-between gap-2">
          <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={upload} data-testid="dates-adenda-input" />
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="dates-adenda-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-200 text-ec-green hover:bg-slate-50 transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? "A ler a adenda…" : "Carregar adenda ao CPCV"}
          </button>
          <button onClick={save} className="px-4 py-2 rounded-lg bg-ec-green text-white font-semibold hover:bg-ec-greenDark transition-colors" data-testid="dates-save">Guardar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
